/**
 * usePopupEventHandler - Hook to handle events from popup windows
 * 
 * This hook listens for messages from popups and:
 * 1. Handles actions (zoom to ship, export, etc.)
 * 2. Responds to POPUP_INIT requests with data
 * 3. Tracks active popups for data push decisions
 * 
 * Should be used once at a high level in the app (e.g., in AppContent)
 */
import { useCallback } from 'react';
import { 
  usePopupMessages,
  MainWindowActions,
  MessageType,
  PopupMessage,
  ViewTargetPayload,
  ZoomToCoordinatesPayload,
  ZoomToPortPayload,
  ExportReportPayload,
  PopupInitPayload,
  GotoEventPayload,
  ShowEventShapePayload,
  AcknowledgeEventPayload,
} from './PopupEvents';
import { useAISMapContext } from '../Map/AISMapContext';
import { useSettings } from '../Settings/SettingsProvider';
import { AutoCompleteShipInfo, ExportFormat, useAcknowledgeEvent, useGotoEventFetch } from '@ais-ui/websockets';
import { PopupType } from '@ais-ui/popups';
import { usePorts } from '../Ports/PortProvider';
import { useShapeFetch } from '@ais-ui/api';
import { shapeToVectorLayer, useLayers, useOpenLayersContext } from '@ais-ui/react-openlayers';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { transform } from 'ol/proj';

/**
 * Active popup registry - tracks which popups are alive
 * Key: popupType, Value: Set of popupIds with their last seen timestamp
 */
const activePopups = new Map<string, Map<string, number>>();
const POPUP_TIMEOUT_MS = 60000; // 1 minute timeout

const registerActivePopup = (popupType: string, popupId: string): void => {
  if (!activePopups.has(popupType)) {
    activePopups.set(popupType, new Map());
  }
  activePopups.get(popupType)!.set(popupId, Date.now());
};

const unregisterPopup = (popupId: string): void => {
  activePopups.forEach((popups) => {
    popups.delete(popupId);
  });
};

const cleanupStalePopups = (): void => {
  const now = Date.now();
  activePopups.forEach((popups) => {
    popups.forEach((lastSeen, popupId) => {
      if (now - lastSeen > POPUP_TIMEOUT_MS) {
        popups.delete(popupId);
      }
    });
  });
};

/**
 * Check if any popup of a given type is currently active
 */
export const isPopupTypeActive = (popupType: string): boolean => {
  cleanupStalePopups();
  const popups = activePopups.get(popupType);
  return popups ? popups.size > 0 : false;
};

/**
 * Check if the targets popup is active (convenience function)
 */
export const isTargetsPopupActive = (): boolean => {
  return isPopupTypeActive(PopupType.Targets);
};

/**
 * Callback registry for data providers
 * Components can register to provide data for specific popup types
 */
type DataProviderCallback = (params: Record<string, unknown>) => unknown | Promise<unknown>;
const dataProviders = new Map<string, DataProviderCallback>();

export const registerPopupDataProvider = (
  popupType: string, 
  callback: DataProviderCallback
): (() => void) => {
  dataProviders.set(popupType, callback);
  return () => dataProviders.delete(popupType);
};

/**
 * Popup types that are handled by dedicated React components
 * These providers use usePopupMessages directly and handle their own responses.
 * We should NOT send error responses for these - let the components handle them.
 */
const componentBasedProviders = new Set<string>([
  'port-timeline',  // PortTimelineDataProvider
  'settings',       // PopupSettingsProvider - settings are sent via usePopupEventHandler
  PopupType.Report, // ReportDataProvider - fetches job and template metadata
  PopupType.Routes, // ArrivalsDataProvider
  PopupType.Events, // EventsDataProvider
  PopupType.VesselTimeline, // VesselTimelineDataProvider
  PopupType.Targets, // TargetsDataProvider - fetches ALL targets (not just current page)
]);

/**
 * Export event subscribers (for report exports triggered from popups)
 */
type ExportEventCallback = (jobId: string, format: ExportFormat) => void;
const exportEventSubscribers = new Set<ExportEventCallback>();

export const subscribeToExportEvent = (callback: ExportEventCallback): (() => void) => {
  exportEventSubscribers.add(callback);
  return () => exportEventSubscribers.delete(callback);
};

const emitExportEvent = (jobId: string, format: ExportFormat): void => {
  exportEventSubscribers.forEach(callback => callback(jobId, format));
};

/**
 * Hook to handle popup events in the main application
 */
export const usePopupEventHandler = (): void => {
  const { setZoomToShip } = useAISMapContext();
  const { olMapRef, settings } = useSettings();
  const { portsByLocode, zoomToPort } = usePorts();
  const { messages } = useLocalization();
  const { toast } = useToast();
  
  // Event action dependencies
  const gotoEventFetch = useGotoEventFetch();
  const shapeFetch = useShapeFetch();
  const { addLayer, doesLayerExist, zoomToLayer } = useLayers();
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const { mutate: acknowledgeEventMutate } = useAcknowledgeEvent();

  const handlePopupMessage = useCallback(async (msg: PopupMessage) => {
    switch (msg.type) {
      case MessageType.POPUP_INIT: {
        const payload = msg.payload as PopupInitPayload;
        
        // Register this popup as active
        registerActivePopup(payload.popupType, payload.popupId);
        
        // Handle settings requests directly
        if (payload.popupType === 'settings') {
          const ribbonSettings = settings['ribbonPlugin.settings'] || {};
          MainWindowActions.sendInitResponse(payload.popupId, {
            dateFormat: ribbonSettings.date_format_mp,
            timeZone: ribbonSettings.time_zone,
          });
          break;
        }
        
        const provider = dataProviders.get(payload.popupType);
        
        if (provider) {
          try {
            const data = await provider(payload.params);
            MainWindowActions.sendInitResponse(payload.popupId, data);
          } catch (err) {
            console.error(`[usePopupEventHandler] Failed to get data for popup ${payload.popupType}:`, err);
            MainWindowActions.sendInitResponse(payload.popupId, { error: 'Failed to load data' });
          }
        } else if (!componentBasedProviders.has(payload.popupType)) {
          // Only send error for popup types not handled by component-based providers
          console.warn(`[usePopupEventHandler] No data provider registered for popup type: ${payload.popupType}`);
          MainWindowActions.sendInitResponse(payload.popupId, { error: 'No data provider' });
        }
        // If componentBasedProviders.has(payload.popupType), do nothing here
        // The dedicated component will handle the response via usePopupMessages
        break;
      }

      case MessageType.POPUP_HEARTBEAT: {
        const payload = msg.payload as { popupId: string; popupType: string };
        registerActivePopup(payload.popupType, payload.popupId);
        // Acknowledge heartbeat so popup knows main window is alive
        MainWindowActions.sendHeartbeatAck(payload.popupId);
        break;
      }

      case MessageType.VIEW_TARGET:
      case MessageType.ZOOM_TO_TARGET: {
        const payload = msg.payload as ViewTargetPayload;
        if (payload.mmsi || payload.name) {
          const targetInfo: AutoCompleteShipInfo = {
            mmsi: payload.mmsi || '',
            name: payload.name || '',
            imo: payload.imo || null,
            callSign: payload.callSign || null,
            type: 'TT_SHIP',
          };
          setZoomToShip(targetInfo);
        }
        break;
      }

      case MessageType.ZOOM_TO_COORDINATES: {
        const payload = msg.payload as ZoomToCoordinatesPayload;
        if (payload.lat !== undefined && payload.lon !== undefined && olMapRef?.current) {
          const view = olMapRef.current.getView();
          const { fromLonLat } = require('ol/proj');
          const coordinates = fromLonLat([payload.lon, payload.lat]);
          
          view.animate({
            center: coordinates,
            zoom: payload.zoom || 12,
            duration: 500,
          });
        }
        break;
      }

      case MessageType.ZOOM_TO_PORT: {
        const payload = msg.payload as ZoomToPortPayload;
        if (payload.locode && olMapRef?.current) {
          const port = portsByLocode.get(payload.locode);
          if (port) {
            zoomToPort(port, olMapRef.current);
          }
        }
        break;
      }

      case MessageType.EXPORT_REPORT: {
        const payload = msg.payload as ExportReportPayload;
        if (payload.jobId && payload.format) {
          emitExportEvent(payload.jobId, payload.format as ExportFormat);
        }
        break;
      }

      case MessageType.GOTO_EVENT: {
        const payload = msg.payload as GotoEventPayload;
        if (payload.eventId && map) {
          try {
            const gotoEventData = await gotoEventFetch(payload.eventId);
            const view = map.getView();
            const transformedCenter = transform(
              [gotoEventData.lon, gotoEventData.lat],
              dataProjection,
              displayProjection
            );
            view.setCenter(transformedCenter);
            view.setZoom(gotoEventData.zoomLevel);
          } catch (error) {
            console.error('[usePopupEventHandler] Failed to go to event:', error);
          }
        }
        break;
      }

      case MessageType.SHOW_EVENT_SHAPE: {
        const payload = msg.payload as ShowEventShapePayload;
        if (payload.shapeUid && payload.detectorName) {
          const layerId = `Detector ${payload.detectorName}`;
          if (!doesLayerExist(layerId)) {
            try {
              const shape = await shapeFetch(payload.shapeUid);
              if (shape) {
                const scopedVectorLayer = shapeToVectorLayer(
                  shape,
                  displayProjection,
                  dataProjection
                );
                addLayer(
                  scopedVectorLayer,
                  layerId,
                  true,
                  true,
                  true,
                  (shape as { properties?: unknown }).properties || {},
                  layerId
                );
                zoomToLayer(scopedVectorLayer);
              }
            } catch (error) {
              console.error('[usePopupEventHandler] Failed to show event shape:', error);
            }
          }
        }
        break;
      }

      case MessageType.ACKNOWLEDGE_EVENT: {
        const payload = msg.payload as AcknowledgeEventPayload;
        if (payload.eventId) {
          acknowledgeEventMutate(payload.eventId, {
            onSuccess: () => {
              toast({
                title: 'Success!',
                description: messages.eventsAcknowledgedEventSuccess,
              });
            },
            onError: () => {
              toast({
                title: 'Error!',
                description: messages.eventsAcknowledgedEventError,
                variant: 'destructive',
              });
            },
          });
        }
        break;
      }

      case MessageType.POPUP_CLOSED: {
        const payload = msg.payload as { popupId: string };
        unregisterPopup(payload.popupId);
        break;
      }
    }
  }, [setZoomToShip, olMapRef, portsByLocode, zoomToPort, settings, map, dataProjection, displayProjection, gotoEventFetch, shapeFetch, doesLayerExist, addLayer, zoomToLayer, acknowledgeEventMutate, toast, messages]);

  usePopupMessages(handlePopupMessage, [handlePopupMessage]);
};
